import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingBasket,
    faEllipsisH,
    faCirclePlus,
    faStethoscope,
} from '@fortawesome/free-solid-svg-icons';
import InputText from '../components/Form/Fields/InputText';
import InputSliderRange from '../components/Form/Fields/InputSliderRange';
import useRangeMax from '../hooks/useRangeMax';
import axios from 'axios';
import { toast } from 'react-toastify';
import CreateMedicalServiceModal from '../components/Modal/CreateMedicalServiceModal';
import Modal from '../components/Modal/Modal';
import { eventBus } from '../bus/eventBus';
import FabGroup from "../components/FabGroup";
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

const formatAmount = (val) =>
    new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const stringToColor = (str = '') => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = '#';
    for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xFF).toString(16)}`).slice(-2);
    return color;
};

const ServiceAvatar = ({ name }) => (
    <span style={{
        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
        background: stringToColor(name || ''), color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15,
    }}>
        <FontAwesomeIcon icon={faStethoscope} />
    </span>
);

export default function Services() {
    const { t } = useTranslation();
    const { hasRight } = useAuth();
    const canCreate = hasRight('SERVICES_CREATE');
    const canEdit = hasRight('SERVICES_EDIT');
    const [selectedMedicalService, setSelectedMedicalService] = useState(null);
    const [showCreateMedicalServiceModal, setShowCreateMedicalServiceModal] = useState(false);
    const [addingToCartId, setAddingToCartId] = useState(null);
    const [quickEditPrice, setQuickEditPrice] = useState(null); // { row, value }
    const priceMax = useRangeMax(`${API_BASE_URL}/api/v1/MedicalServices`, 'price');
    const { setTitle, setTitleIcon } = usePageTitle();
    const tableRef = useRef(null);

    useEffect(() => {
        setTitle(`${t('Medical Services')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('services.subtitle')}</span>`);
        document.title = t('Medical Services');
        setTitleIcon('ti-stethoscope');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Medical Services'), url: "/services" }]);

    const fetchOrderableForCart = async (type, id) => {
        const { data } = await axios.get(
            `${API_BASE_URL}/api/v1/cart/${type}/${id}`,
            { withCredentials: true }
        );
        return data;
    };

    const addToCart = async (id) => {
        setAddingToCartId(id);
        try {
            const item = await fetchOrderableForCart("service", id);
            eventBus.emit('newCartItem', item);
        } catch {
            toast.error(t('product.error_add_to_cart'));
        } finally {
            setAddingToCartId(null);
        }
    };

    const handleEdit = (row) => {
        setSelectedMedicalService(row);
        setShowCreateMedicalServiceModal(true);
    };

    const handleQuickPriceSave = async () => {
        if (!quickEditPrice) return;
        const newPrice = parseFloat(quickEditPrice.value);
        if (isNaN(newPrice) || newPrice < 0) return;

        try {
            await axios.put(`${API_BASE_URL}/api/v1/updateMedicalService`, {
                ...quickEditPrice.row,
                price: newPrice,
            }, { withCredentials: true });
            setQuickEditPrice(null);
            tableRef.current?.reload();
        } catch {
            toast.error(t('services.toast.price_update_error'));
        }
    };

    const columns = [
        {
            Header: "#",
            accessor: "id",
            sortable: true,
            width: "70px",
            maxWidth: "70px",
            filterable: { component: InputText },
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>,
        },
        {
            Header: t('services.column.name'),
            accessor: "name",
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <ServiceAvatar name={row.name} />
                    <div>
                        <span className="cell-name">{row.name}</span>
                        {row.description && <span className="cell-sub">{row.description}</span>}
                    </div>
                </div>
            ),
        },
        {
            Header: t('services.column.price'),
            accessor: "price",
            className: "text-end",
            sortable: true,
            activeColumn: true,
            filterable: { component: InputSliderRange, min: 0, max: priceMax, step: 1, unit: 'dhs' },
            // same look as the products listing's price column (productPriceColumn)
            Cell: (row) => (
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 4 }}>
                    {row.isCommon || !canEdit ? (
                        <span style={{ fontSize: 13.5, color: "#111827", fontWeight: 600 }}>
                            {formatAmount(row.price)}
                        </span>
                    ) : (
                        <span
                            className="cell-price-editable"
                            style={{ fontSize: 13.5, color: "#000", cursor: "pointer", borderBottom: "1px dashed #ced4da", fontWeight: 600 }}
                            data-tooltip-id="tooltip"
                            data-tooltip-content={t('services.tooltip.edit_price')}
                            onClick={(e) => { e.stopPropagation(); setQuickEditPrice({ row, value: String(row.price ?? '') }); }}
                        >
                            {formatAmount(row.price)}
                        </span>
                    )}
                    <span style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 500 }}>dhs</span>
                </div>
            ),
        },
        {
            Header: t('Orders'),
            accessor: "orderCount",
            className: "text-center",
            sortable: true,
            Cell: (row) => (
                row.orderCount > 0 ? (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        fontSize: 11.5, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff',
                        padding: '3px 9px', borderRadius: 999,
                    }}>
                        {row.orderCount}
                    </span>
                ) : <span className="cell-muted">—</span>
            ),
        },
        {
            Header: "",
            accessor: "actions",
            className: "col-actions",
            Cell: (row) => (
                <div className="cell-dropdown" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <button
                        className="cell-actions-btn"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={t('Add to cart')}
                        onClick={() => addToCart(row.id)}
                        disabled={addingToCartId === row.id}
                    >
                        {addingToCartId === row.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <FontAwesomeIcon icon={faShoppingBasket} />}
                    </button>
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => addToCart(row.id)}>
                                <FontAwesomeIcon icon={faShoppingBasket} className="me-2 text-muted" />
                                {t('Add to cart')}
                            </button>
                        </li>
                        {!row.isCommon && canEdit && (
                            <>
                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <button className="dropdown-item" onClick={() => handleEdit(row)}>
                                        {t('services.action.edit_service')}
                                    </button>
                                </li>
                                <li>
                                    <button className="dropdown-item danger">{t('Delete')}</button>
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderServiceCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div className="d-flex align-items-center gap-3">
                    <ServiceAvatar name={row.name} />
                    <div style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', display: 'block' }}>{row.name}</span>
                        {row.description && (
                            <span style={{ fontSize: 11.5, color: '#9ca3af', display: 'block' }}>{row.description}</span>
                        )}
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{formatAmount(row.price)}</span>
                        <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                        {row.orderCount > 0 && (
                            <span style={{
                                marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff',
                                padding: '2px 8px', borderRadius: 999,
                            }}>
                                {row.orderCount}
                            </span>
                        )}
                    </div>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    const buttons = canCreate ? [
        { label: t('New Service'), className: 'btn-primary', icon: faCirclePlus, onClick: () => {
            setShowCreateMedicalServiceModal(true);
            setSelectedMedicalService(null);
        }},
    ] : [];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/MedicalServices`}
                columns={columns}
                defaultSort="id"
                defaultDirection="desc"
                pageSize={20}
                emptyMessage={t('services.empty')}
                searchable={true}
                multiSelect={true}
                searchPlaceholder={t('services.search_placeholder')}
                getRowDragData={(row) => ({ type: 'service', id: row.id, name: row.name })}
                enableCardView={true}
                renderCard={renderServiceCard}
            />

            <CreateMedicalServiceModal
                medicalService={selectedMedicalService}
                show={showCreateMedicalServiceModal}
                onClose={() => { setShowCreateMedicalServiceModal(false); setSelectedMedicalService(null); }}
                onSave={() => tableRef.current?.reload()}
                defaultValues={selectedMedicalService || {}}
            />

            <FabGroup buttons={buttons} />

            <Modal
                show={!!quickEditPrice}
                title={t('services.column.price')}
                onClose={() => setQuickEditPrice(null)}
                classSize="modal-sm"
                footer={
                    <>
                        <button className="btn btn-light btn-sm" onClick={() => setQuickEditPrice(null)}>{t('Cancel')}</button>
                        <button className="btn btn-primary btn-sm fw-bold" onClick={handleQuickPriceSave}>{t('Save')}</button>
                    </>
                }
            >
                {quickEditPrice && (
                    <input
                        autoFocus
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={quickEditPrice.value}
                        onChange={e => setQuickEditPrice(q => ({ ...q, value: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') handleQuickPriceSave(); if (e.key === 'Escape') setQuickEditPrice(null); }}
                    />
                )}
            </Modal>
        </>
    );
}
